import React, { useEffect, useState } from 'react';

import { getEditorWorkflowRoute, Select } from '@aline/common';
import { link } from '@aline/routes';
import type { TooltipProps } from '@mui/material';
import { Popover, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthGuard } from 'src/auth';
import type { AlineButtonProps } from 'src/components';
import { AlineButton, AlineTooltip, ConfirmationDialog, SanityCheckModal } from 'src/components';
import { useGetIsInboxWorkflow, WorkflowInboxFilter } from 'src/components/dashboard';
import type { WorkflowInboxWorkflow } from 'src/components/dashboard';
import { DownArrowIcon } from 'src/components/icons/DownArrowIcon';
import { DownloadIcon } from 'src/components/icons/DownloadIcon';
import { SmallLaunchWorkflowIcon } from 'src/components/icons/SmallLaunchWorkflowIcon';
import { SmallRevertToDraftIcon } from 'src/components/icons/SmallRevertToDraftIcon';
import { SmallShareIcon } from 'src/components/icons/SmallShareIcon';
import { SmallSignatureIcon } from 'src/components/icons/SmallSignatureIcon';
import { WorkflowWhatsNextContent } from 'src/components/workflow/workflow-builder/WorkflowWhatsNext';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import {
  useCheckWorkflowAuthorization,
  useExportWorkflowFiles,
  useGetWorkflowFiles,
  useRevertWorkflowToDraft,
  useSendForSignatureWithChecks,
} from 'src/hooks';
import type { ReactSetStateTuple } from 'src/types';
import { getWorkflowIsSigned, getWorkflowWasSentForSig } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

const useButtonStyles = makeStyles()(theme => ({
  buttonContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  circle: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderRadius: '50%',
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  popover: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: 420,
    padding: theme.spacing(2),
    width: 500,
  },
  tooltipWrapper: {
    height: 'fit-content',
  },
}));

interface PrimaryButtonProps {
  workflow: WorkflowInboxWorkflow;
}

export function PrimaryWorkflowButton({ workflow }: PrimaryButtonProps): JSX.Element | null {
  const { getIsInboxWorkflow } = useGetIsInboxWorkflow();
  const isWorkflowSigned = getWorkflowIsSigned(workflow ?? null);
  const wasSentForSig = getWorkflowWasSentForSig(workflow ?? null);
  const [actionsOpen, setActionsOpen] = useState<boolean>(false);
  const [inboxWorkflowInfo, setInboxWorkflowInfo] = useState<{
    inboxActionCount: number;
    isInboxWorkflow: boolean;
  }>({ inboxActionCount: 0, isInboxWorkflow: false });
  const isTemplateWorkflow = workflow.state === Apollo.WorkflowState.Template;
  const isReadyForSig = workflow.state === Apollo.WorkflowState.ReadyForSignature && !wasSentForSig;
  const isWorkflowCompleted = workflow.state === Apollo.WorkflowState.Complete && isWorkflowSigned;
  const canRevertToDraft = wasSentForSig && !isWorkflowSigned;

  useEffect(() => {
    if (!actionsOpen) {
      const { inboxActionCount, isInboxWorkflow } = getIsInboxWorkflow(
        workflow,
        WorkflowInboxFilter.All
      );
      setInboxWorkflowInfo({ inboxActionCount, isInboxWorkflow });
    }
  }, [actionsOpen, getIsInboxWorkflow, workflow]);

  if (isTemplateWorkflow) return <LaunchTemplateWorkflowButton workflow={workflow} />;

  if (inboxWorkflowInfo.isInboxWorkflow)
    return (
      <WorkflowRequiredActionsButton
        inboxActionCount={inboxWorkflowInfo.inboxActionCount}
        openTuple={[actionsOpen, setActionsOpen]}
        workflow={workflow}
      />
    );

  if (isReadyForSig) return <SendForSignatureButton workflow={workflow} />;

  if (canRevertToDraft) return <RevertWorkflowToDraftButton workflow={workflow} />;

  if (isWorkflowCompleted) return <DownloadWorkflowButton workflow={workflow} />;

  return <ShareWorkflowButton workflow={workflow} />;
}

interface ShareWorkflowButtonProps {
  workflow: Pick<Apollo.Workflow, 'id' | '__typename'>;
}

function ShareWorkflowButton({ workflow }: ShareWorkflowButtonProps) {
  const navigate = useNavigate();
  const { classes } = useButtonStyles();

  return (
    <AlineButton
      color="primary"
      data-cy={Select.workflow.detail.toolbar.shareWorkflowButton}
      variant="contained"
      onClick={() => {
        navigate(link.WorkflowDetailTabCollaborators({ workflowId: workflow.id }));
      }}
    >
      <div className={classes.buttonContent}>
        <SmallShareIcon fill="currentColor" height={20} width={20} />
        Share Workflow
      </div>
    </AlineButton>
  );
}
interface LaunchTemplateWorkflowButtonProps {
  workflow: Pick<Apollo.Workflow, 'id' | 'publishedAt' | '__typename'>;
  buttonProps?: Partial<AlineButtonProps>;
  tooltipPlacement?: TooltipProps['placement'];
}

export function LaunchTemplateWorkflowButton({
  buttonProps,
  tooltipPlacement = 'bottom',
  workflow,
}: LaunchTemplateWorkflowButtonProps): JSX.Element {
  const navigate = useNavigate();
  const { classes } = useButtonStyles();
  const isPublished = Boolean(workflow.publishedAt);

  return (
    <AlineTooltip
      arrow
      placement={tooltipPlacement}
      title={!isPublished ? 'Publish template to launch' : ''}
    >
      <div className={classes.tooltipWrapper}>
        <AlineButton
          color="primary"
          data-cy={Select.workflow.detail.toolbar.launchWorkflowButton}
          disabled={!isPublished}
          variant="contained"
          onClick={() => {
            navigate(
              link.QuickStartLaunchTemplateSelectedTemplate({ templateWorkflowId: workflow.id })
            );
          }}
          {...buttonProps}
        >
          <div className={classes.buttonContent}>
            <SmallLaunchWorkflowIcon fill="currentColor" />
            Launch Workflow
          </div>
        </AlineButton>
      </div>
    </AlineTooltip>
  );
}

interface RevertToDraftButtonProps {
  workflow: Pick<Apollo.Workflow, 'id' | '__typename'>;
  buttonProps?: Partial<AlineButtonProps>;
}

export function RevertWorkflowToDraftButton({
  buttonProps,
  workflow,
}: RevertToDraftButtonProps): JSX.Element {
  const { classes } = useButtonStyles();
  const [open, setOpen] = useState<boolean>(false);
  const { revertToDraft } = useRevertWorkflowToDraft();
  const { guardedCallback: handleOpen } = useAuthGuard(() => setOpen(!open));

  async function handleRevertToDraft(): Promise<void> {
    await revertToDraft({
      variables: { workflowId: workflow.id },
    });
  }

  return (
    <>
      <AlineButton
        color="primary"
        data-cy={Select.workflow.detail.toolbar.revertToDraftButton}
        variant="contained"
        onClick={handleOpen}
        {...buttonProps}
      >
        <div className={classes.buttonContent}>
          <SmallRevertToDraftIcon stroke="currentColor" />
          Revert to Draft
        </div>
      </AlineButton>
      <ConfirmationDialog
        message="Are you sure you want to revert the workflow back to draft? All documents and the workflow will be retained, but any progress in the signing section will be cleared. This action cannot be undone."
        open={open}
        primaryText="Revert"
        title="Revert to drafting?"
        onClickPrimary={handleRevertToDraft}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

interface SendForSignatureButtonProps {
  workflow: Pick<Apollo.Workflow, 'id' | 'isSignedOutsideAline' | '__typename'>;
  buttonProps?: Partial<AlineButtonProps>;
  tooltipPlacement?: TooltipProps['placement'];
}

export function SendForSignatureButton({
  buttonProps,
  tooltipPlacement = 'bottom',
  workflow,
}: SendForSignatureButtonProps): JSX.Element {
  const navigate = useNavigate();
  const { classes } = useButtonStyles();
  const dispatchNoFile = useDispatchNotification({
    message: 'No workflow file found.',
    variant: 'error',
  });
  const { workflowFiles } = useGetWorkflowFiles(workflow.id);
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflow.id);
  const { isSubmitting, openDialog, sendForSignatureWithChecks, setOpenDialog } =
    useSendForSignatureWithChecks({
      showNotReadyReasons: true,
      workflowId: workflow.id,
    });
  const sendDisabledReason = !canEditWorkflow
    ? 'Only the workflow owner and editors are authorized to send for signature'
    : workflow.isSignedOutsideAline
      ? 'Workflow is being signed outside Aline'
      : '';
  const { guardedCallback: handleSendForSignature } = useAuthGuard(sendForSignatureWithChecks);
  const isSendDisabled = Boolean(sendDisabledReason);

  return (
    <>
      <AlineTooltip arrow placement={tooltipPlacement} title={sendDisabledReason}>
        <div className={classes.tooltipWrapper}>
          <AlineButton
            color="primary"
            data-cy={Select.workflow.detail.toolbar.sendForSignatureButton}
            disabled={isSendDisabled}
            loading={isSubmitting}
            variant="contained"
            onClick={handleSendForSignature}
            {...buttonProps}
          >
            <div className={classes.buttonContent}>
              <SmallSignatureIcon stroke="currentColor" />
              Start Signing
            </div>
          </AlineButton>
        </div>
      </AlineTooltip>
      <SanityCheckModal
        openDialogTuple={[openDialog, setOpenDialog]}
        workflowId={workflow.id}
        onClickFixMissingActions={() => {
          const firstFile = workflowFiles.at(0);
          if (!firstFile) return dispatchNoFile();

          navigate(getEditorWorkflowRoute(firstFile.document.id, workflow.id));
        }}
        onClose={() => setOpenDialog(null)}
      />
    </>
  );
}

export function DownloadWorkflowButton({
  buttonProps,
  workflow,
}: {
  workflow: Pick<Apollo.Workflow, 'id' | '__typename'>;
  buttonProps?: Partial<AlineButtonProps>;
}) {
  const { classes } = useButtonStyles();
  const { exportToPdf, loading: exporting } = useExportWorkflowFiles(workflow.id);

  return (
    <AlineButton
      color="primary"
      loading={exporting}
      variant="contained"
      onClick={exportToPdf}
      {...buttonProps}
    >
      <div className={classes.buttonContent}>
        <DownloadIcon stroke="currentColor" style={{ height: 20, width: 20 }} />
        Download
      </div>
    </AlineButton>
  );
}

function WorkflowRequiredActionsButton({
  inboxActionCount,
  openTuple: [open, setOpen],
  workflow,
}: {
  inboxActionCount: number;
  openTuple: ReactSetStateTuple<boolean>;
  workflow: WorkflowInboxWorkflow;
}) {
  const { classes } = useButtonStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  function handleClose() {
    setOpen(false);
  }

  return (
    <>
      <AlineButton
        ref={setAnchorEl}
        color="primary"
        endIcon={<DownArrowIcon />}
        variant="contained"
        onClick={() => setOpen(true)}
      >
        <div className={classes.buttonContent}>
          <div className={classes.circle}>
            <Typography color="primary">{inboxActionCount}</Typography>
          </div>
          Actions
        </div>
      </AlineButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        classes={{ paper: classes.popover }}
        open={open}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        onClose={handleClose}
      >
        <WorkflowWhatsNextContent workflow={workflow} onClose={handleClose} />
      </Popover>
    </>
  );
}
